import { useCallback, useEffect, useRef, useState } from "react";
import { requestTimeout } from "../lib/requestTimeout";
import type { BuilderApi, ConfirmationDecision, Conversation, OpenPost, PostAsk, PostRewriteDecision, ProductSuggestionDecision, EditorContext } from "../api/contract";
import { toBuilderError } from "../api/errors";
import { DEFAULT_REFERENCE_TEXT, messageProblem, newMessageId, sendOutcome } from "../lib/conversationState";
import type { SentReference } from "./useReferences";

/**
 * A message the server has not yet confirmed. It keeps one ID for every resend, so a retry after an
 * unknown outcome can never create a second request.
 * - `sending`: a request is in flight.
 * - `checking`: the outcome is unknown; it will be checked and resent automatically.
 * - `unconfirmed`: automatic resends are used up; the owner can send it again.
 */
export interface Outbox {
  messageId: string;
  text: string;
  /** The page on screen when it was sent; resends keep it. */
  pagePath?: string;
  editorContext?: EditorContext;
  /** Inspiration images sent with it; resends keep them. */
  attachments?: SentReference[];
  /** The writing view's request this message carries; resends keep it. */
  post?: PostAsk;
  openPost?: OpenPost;
  prepared?: boolean;
  status: "sending" | "checking" | "unconfirmed";
  attempts: number;
}

/** Why the owner's words were handed back to the composer. */
export type ChatNotice = "busy" | "limited" | "refused" | "switched-off";

export interface UseConversationOptions {
  api: BuilderApi;
  siteId: string;
  /** The draft the workspace currently shows. */
  workspaceDraftId: string | undefined;
  refreshWorkspace: () => Promise<unknown>;
  /** The page the owner is looking at, sent with each new message. */
  pagePath?: string;
  editorContext?: EditorContext;
  prepareOpenPost?: () => Promise<OpenPost | null>;
  /** Poll delays in ms while a reply is in progress; the last value repeats. */
  pollDelays?: number[];
  /** Delay before each automatic resend of a message with an unknown outcome. */
  retryDelays?: number[];
  /** A message that wasn't accepted gives its inspiration images back, like its words. */
  onAttachmentsReturned?: (attachments: SentReference[]) => void;
}

const DEFAULT_POLL_DELAYS = [1000, 1500, 2500, 4000];
const DEFAULT_RETRY_DELAYS = [1500, 4000, 8000];
const MAX_BACKOFF = 15_000;

/**
 * The persisted administrator conversation. The server owns the transcript and every turn; this hook
 * only shows its answers, polls while a reply is in progress, and resends unknown outcomes with the
 * same message ID. A refused session or site clears everything private it holds.
 */
export function useConversation({
  api, siteId, workspaceDraftId, refreshWorkspace, pagePath, editorContext, prepareOpenPost,
  pollDelays = DEFAULT_POLL_DELAYS, retryDelays = DEFAULT_RETRY_DELAYS, onAttachmentsReturned,
}: UseConversationOptions) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [connection, setConnection] = useState<"loading" | "connected" | "reconnecting">("loading");
  const [failure, setFailure] = useState<"signed-out" | "not-found" | null>(null);
  const [draft, setDraft] = useState("");
  const [outbox, setOutboxState] = useState<Outbox | null>(null);
  const [notice, setNotice] = useState<ChatNotice | null>(null);
  const [deciding, setDeciding] = useState<ConfirmationDecision["decision"] | null>(null);
  const [decisionFailed, setDecisionFailed] = useState(false);
  // Completed reads; each one lets the poll effect schedule the next.
  const [reads, setReads] = useState(0);

  // Every request takes a ticket; an answer older than the last one shown is ignored.
  const tickets = useRef(0);
  const shown = useRef(0);
  const revoked = useRef(false);
  // Bumped when the hook unmounts or moves to another site (StrictMode included). An async step that
  // started under an older epoch stops at its next await, so nothing is sent or shown for a closed session.
  const epoch = useRef(0);
  const failures = useRef(0);
  const attempt = useRef(0);
  const outboxRef = useRef<Outbox | null>(null);
  // The latest callback, so a new one never restarts a send in progress.
  const returnAttachments = useRef(onAttachmentsReturned);
  returnAttachments.current = onAttachmentsReturned;
  const giveBack = useCallback((entry: Outbox) => {
    if (entry.attachments?.length) returnAttachments.current?.(entry.attachments);
  }, []);

  const setOutbox = useCallback((next: Outbox | null) => {
    outboxRef.current = next;
    setOutboxState(next);
  }, []);

  const revoke = useCallback((reason: "signed-out" | "not-found") => {
    revoked.current = true;
    setFailure(reason);
    setConversation(null);
    setOutbox(null);
    setDraft("");
    setNotice(null);
  }, [setOutbox]);

  const show = useCallback((next: Conversation, ticket: number) => {
    if (revoked.current || ticket < shown.current) return;
    shown.current = ticket;
    failures.current = 0;
    setConversation(next);
    setConnection("connected");
    const pending = outboxRef.current;
    if (pending && next.messages.some((message) => message.id === pending.messageId)) setOutbox(null);
  }, [setOutbox]);

  const read = useCallback(async (): Promise<Conversation | null> => {
    if (revoked.current) return null;
    const run = epoch.current;
    const ticket = ++tickets.current;
    try {
      const next = await requestTimeout(() => api.getConversation(siteId));
      if (run !== epoch.current) return null;
      show(next, ticket);
      return revoked.current ? null : next;
    } catch (caught) {
      if (run !== epoch.current || revoked.current) return null;
      const error = toBuilderError(caught);
      if (error.failure === "signed-out" || error.failure === "not-found") {
        revoke(error.failure);
        return null;
      }
      failures.current += 1;
      setConnection("reconnecting");
      return null;
    } finally {
      if (run === epoch.current) setReads((count) => count + 1);
    }
  }, [api, siteId, show, revoke]);

  const deliver = useCallback(async (entry: Outbox) => {
    if (revoked.current) return;
    const run = epoch.current;
    setOutbox({ ...entry, status: "sending" });
    const ticket = ++tickets.current;
    let sent = false;
    try {
      if (!entry.post && !entry.prepared && prepareOpenPost) {
        const openPost = await prepareOpenPost();
        entry = { ...entry, prepared: true, ...(openPost ? { openPost } : {}) };
        setOutbox(entry);
      }
      sent = true;
      const next = await api.sendMessage(siteId, {
        messageId: entry.messageId, text: entry.text, ...(entry.pagePath ? { pagePath: entry.pagePath } : {}),
        ...(entry.attachments?.length ? { attachments: entry.attachments.map((attachment) => ({ asset: attachment.asset })) } : {}),
        ...(entry.post ? { post: entry.post } : {}),
        ...(entry.openPost ? { openPost: entry.openPost } : {}),
        ...(entry.editorContext ? { editorContext: entry.editorContext } : {}),
      });
      if (run !== epoch.current || revoked.current) return;
      if (outboxRef.current?.messageId === entry.messageId) setOutbox(null);
      setNotice(null);
      show(next, ticket);
    } catch (caught) {
      if (run !== epoch.current || revoked.current || outboxRef.current?.messageId !== entry.messageId) return;
      const outcome = sent ? sendOutcome(caught) : "refused";
      if (outcome === "signed-out" || outcome === "not-found") return revoke(outcome);
      if (outcome === "unknown") {
        const attempts = entry.attempts + 1;
        setOutbox({ ...entry, attempts, status: attempts > retryDelays.length ? "unconfirmed" : "checking" });
        return;
      }
      // Definitely not accepted: give the words (and images) back, without overwriting anything typed since.
      setOutbox(null);
      setDraft((current) => (current.trim() ? current : entry.attachments?.length && entry.text === DEFAULT_REFERENCE_TEXT ? current : entry.text));
      giveBack(entry);
      setNotice(outcome);
      void read();
    }
  }, [api, siteId, show, revoke, read, retryDelays.length, setOutbox, giveBack, prepareOpenPost]);

  // First read.
  useEffect(() => {
    void read();
  }, [read]);

  // Returning to the tab or the network reads the server again.
  useEffect(() => {
    const again = () => {
      if (document.visibilityState !== "hidden") void read();
    };
    window.addEventListener("focus", again);
    window.addEventListener("online", again);
    document.addEventListener("visibilitychange", again);
    return () => {
      window.removeEventListener("focus", again);
      window.removeEventListener("online", again);
      document.removeEventListener("visibilitychange", again);
    };
  }, [read]);

  // Poll while a reply is in progress, and back off while the server can't be reached.
  const replying = conversation?.status === "replying";
  const needsPoll = failure === null && connection !== "loading" && (replying || connection === "reconnecting");
  useEffect(() => {
    if (!needsPoll) {
      attempt.current = 0;
      return;
    }
    const delay = failures.current > 0
      ? Math.min(MAX_BACKOFF, 2000 * 2 ** (failures.current - 1))
      : pollDelays[Math.min(attempt.current, pollDelays.length - 1)]!;
    attempt.current += 1;
    const timer = setTimeout(() => void read(), delay);
    return () => clearTimeout(timer);
  }, [reads, needsPoll, read, pollDelays]);

  // Check an unknown outcome, then resend the same message if the server doesn't have it.
  useEffect(() => {
    if (outbox?.status !== "checking") return;
    const delay = retryDelays[Math.min(outbox.attempts - 1, retryDelays.length - 1)] ?? 0;
    const run = epoch.current;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const latest = await read();
      // clearTimeout can't stop a check that already started; this can.
      if (cancelled || run !== epoch.current) return;
      const current = outboxRef.current;
      if (!current || current.messageId !== outbox.messageId || current.status !== "checking") return;
      if (latest?.status === "unavailable") {
        setOutbox(null);
        setDraft((text) => (text.trim() ? text : current.text));
        giveBack(current);
        setNotice("switched-off");
        return;
      }
      void deliver(current);
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [outbox, read, deliver, retryDelays, setOutbox, giveBack]);

  // The server's canonical draft moved (e.g. an edit or an applied design): show it.
  const serverDraft = conversation?.draftId;
  const refreshedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!serverDraft || !workspaceDraftId || serverDraft === workspaceDraftId || refreshedFor.current === serverDraft) return;
    refreshedFor.current = serverDraft;
    void refreshWorkspace();
  }, [serverDraft, workspaceDraftId, refreshWorkspace]);

  // A finished turn changes recent activity (a design it applied, content it changed, and so on).
  const pendingTurn = conversation?.pendingTurnId ?? null;
  const previous = useRef<{ pendingTurn: string | null } | null>(null);
  useEffect(() => {
    if (conversation === null) return;
    const before = previous.current;
    previous.current = { pendingTurn };
    if (before && before.pendingTurn !== null && pendingTurn === null) void refreshWorkspace();
  }, [conversation, pendingTurn, refreshWorkspace]);

  // The workspace moved on its own: re-read so any review reflects the new base.
  const lastWorkspaceDraft = useRef(workspaceDraftId);
  useEffect(() => {
    if (lastWorkspaceDraft.current !== undefined && workspaceDraftId !== lastWorkspaceDraft.current) void read();
    lastWorkspaceDraft.current = workspaceDraftId;
  }, [workspaceDraftId, read]);

  // One session per site. Leaving it (unmount, StrictMode's rehearsal, or another site) ends every
  // pending continuation and drops the previous site's private state before the next read.
  useEffect(() => () => {
    epoch.current += 1;
    revoked.current = false;
    shown.current = tickets.current;
    failures.current = 0;
    attempt.current = 0;
    refreshedFor.current = null;
    previous.current = null;
    setConversation(null);
    setConnection("loading");
    setFailure(null);
    setOutbox(null);
    setDraft("");
    setNotice(null);
  }, [api, siteId, setOutbox]);

  // A waiting confirmation is answered with its buttons; the composer waits until then.
  const confirmation = conversation?.confirmation ?? null;
  const ready = failure === null && (conversation?.status === "ready" || conversation?.status === "awaiting-input") && outbox === null && confirmation === null;
  const canSend = ready && messageProblem(draft) === null;

  /**
   * Sends the draft (or `text`), with any inspiration images. With images, the words may be left out;
   * the message then says it is inspiration. Answers whether it was sent, so the caller can let go of the images.
   */
  const send = useCallback((text?: string, attachments: SentReference[] = [], post?: PostAsk): boolean => {
    const typed = (text ?? draft).trim();
    const message = typed || (attachments.length ? DEFAULT_REFERENCE_TEXT : "");
    // The ref, not render state: a second call in the same tick must not allocate another message.
    if (!ready || outboxRef.current !== null || revoked.current || messageProblem(message) !== null) return false;
    if (text === undefined) setDraft("");
    setNotice(null);
    void deliver({ messageId: newMessageId(), text: message, status: "sending", attempts: 0, ...(pagePath ? { pagePath } : {}), ...(editorContext ? { editorContext } : {}), ...(attachments.length ? { attachments } : {}), ...(post ? { post } : {}) });
    return true;
  }, [draft, ready, deliver, pagePath, editorContext]);

  /** Resend an unconfirmed message with its original ID. */
  const retrySend = useCallback(() => {
    const current = outboxRef.current;
    if (current?.status === "unconfirmed") void deliver({ ...current, attempts: 0 });
  }, [deliver]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  /** Shows a conversation another request answered with (sending comments), and polls from there. */
  const adopt = useCallback((next: Conversation) => {
    if (revoked.current) return;
    show(next, ++tickets.current);
    setReads((count) => count + 1);
  }, [show]);

  /**
   * Answers the waiting confirmation. The server records one answer per confirmation, so an unknown
   * outcome is safe to resend once; the conversation it returns is shown straight away.
   */
  const decide = useCallback(async (decision: ConfirmationDecision["decision"], alternativeId?: string) => {
    const pending = confirmation;
    if (!pending || deciding !== null || revoked.current) return;
    const run = epoch.current;
    setDeciding(decision);
    setDecisionFailed(false);
    const body = { turnId: pending.turnId, confirmationId: pending.id, decision, ...(alternativeId ? { alternativeId } : {}) };
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const next = await api.decideConfirmation(siteId, body);
        if (run !== epoch.current) return;
        adopt(next);
        setDeciding(null);
        return;
      } catch (caught) {
        if (run !== epoch.current || revoked.current) return;
        const outcome = sendOutcome(caught);
        if (outcome === "signed-out" || outcome === "not-found") return revoke(outcome);
        if (outcome !== "unknown") break;
      }
    }
    setDeciding(null);
    setDecisionFailed(true);
    void read();
  }, [api, siteId, confirmation, deciding, adopt, revoke, read]);

  /**
   * Tells the chat what the owner did with a suggestion for selected words. The server records it once,
   * and the conversation it returns is shown at once. Answers whether it was recorded.
   */
  const decidePostRewrite = useCallback(async (decision: PostRewriteDecision): Promise<boolean> => {
    if (revoked.current) return false;
    const run = epoch.current;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const next = await api.decidePostRewrite(siteId, decision);
        if (run !== epoch.current) return false;
        adopt(next);
        return true;
      } catch (caught) {
        if (run !== epoch.current || revoked.current) return false;
        const outcome = sendOutcome(caught);
        if (outcome === "signed-out" || outcome === "not-found") { revoke(outcome); return false; }
        if (outcome !== "unknown") break;
      }
    }
    void read();
    return false;
  }, [api, siteId, adopt, revoke, read]);

  const decideProductSuggestion = useCallback(async (decision: ProductSuggestionDecision): Promise<boolean> => {
    if (revoked.current) return false;
    const run = epoch.current;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const next = await api.decideProductSuggestion(siteId, decision);
        if (run !== epoch.current) return false;
        adopt(next);
        return true;
      } catch (caught) {
        if (run !== epoch.current || revoked.current) return false;
        const outcome = sendOutcome(caught);
        if (outcome === "signed-out" || outcome === "not-found") { revoke(outcome); return false; }
        if (outcome !== "unknown") break;
      }
    }
    void read();
    return false;
  }, [api, siteId, adopt, revoke, read]);

  return {
    decideProductSuggestion,
    decidePostRewrite,
    conversation, connection, failure, draft, setDraft, outbox, notice,
    /** A change waiting for the owner's go-ahead, and the answer being sent for it. */
    confirmation, deciding, decisionFailed, decide,
    /** A message can go now (ignoring what is typed). */
    ready,
    canSend, send, retrySend, refresh: read, dismissNotice, adopt,
    /** Another request learned access ended: drop everything private and stop reading. */
    forget: revoke,
  };
}

export type ConversationController = ReturnType<typeof useConversation>;
